import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { adminApi } from '../../services/api';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmModal from '../../components/common/ConfirmModal';
// ── AI Summary — plug-and-play (remove this import to hide the panel) ────────
import AiSummaryPanel from '../../features/ai-summary/AiSummaryPanel';

export default function CompanyReview() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [modal, setModal] = useState(null); // { assignmentId, approve }
  const [notes, setNotes]  = useState('');

  const { data, isLoading } = useQuery(
    ['admin-review-queue'],
    () => adminApi.getFinishedGoods({ status: 'VENDOR_SUBMITTED', size: 50 }),
    { refetchInterval: 30000 }
  );

  const reviewMutation = useMutation(adminApi.companyReview, {
    onSuccess: (_, vars) => {
      toast.success(vars.approved ? 'Submission approved!' : 'Submission rejected.');
      qc.invalidateQueries('admin-review-queue');
      qc.invalidateQueries('admin-stats');
      setModal(null);
      setNotes('');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Review failed'),
  });

  const content = data?.data?.content || [];

  const openModal = (assignmentId, approve) => { setModal({ assignmentId, approve }); setNotes(''); };
  const handleConfirm = () => reviewMutation.mutate({ assignmentId: modal.assignmentId, approved: modal.approve, notes });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">OmniDrive Review Queue</h1>
        <p className="text-sm text-gray-500 mt-0.5">Vendor submissions awaiting OmniDrive review before sending to clients</p>
      </div>

      {isLoading ? <LoadingSpinner /> : content.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
          <p className="text-4xl mb-3">✅</p>
          <p className="font-medium text-gray-900">Review queue is empty</p>
          <p className="text-sm text-gray-400 mt-1">No vendor submissions pending OmniDrive review</p>
        </div>
      ) : (
        <div className="space-y-4">
          {content.map((fg) =>
            fg.assignments?.filter(a => a.status === 'VENDOR_SUBMITTED').map((asgn) => (
              <div key={asgn.assignmentId} className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{fg.fgName}</h3>
                      <StatusBadge status={asgn.status} />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Vendor: <span className="font-medium text-gray-700">{asgn.vendorName}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => navigate(`/platform/assignments/${asgn.assignmentId}`)}
                      className="px-3 py-1.5 text-sm border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors font-medium">
                      View BOM →
                    </button>
                    <button onClick={() => openModal(asgn.assignmentId, false)}
                      className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors">
                      Reject
                    </button>
                    <button onClick={() => openModal(asgn.assignmentId, true)}
                      className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                      Approve
                    </button>
                  </div>
                </div>
                {/* ── AI Summary Panel — renders nothing when feature is disabled ── */}
                <AiSummaryPanel assignmentId={asgn.assignmentId} />
              </div>
            ))
          )}
        </div>
      )}

      {/* Review Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="font-semibold text-gray-900 text-lg mb-1">
              {modal.approve ? '✅ OmniDrive Approval' : '❌ OmniDrive Rejection'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {modal.approve
                ? 'OmniDrive is approving this submission. It will be forwarded to the client for final review.'
                : 'OmniDrive is rejecting this submission. The vendor will be notified to revise and resubmit.'}
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              placeholder="Add notes for the vendor or client..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => setModal(null)} disabled={reviewMutation.isLoading}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleConfirm} disabled={reviewMutation.isLoading}
                className={`px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 ${modal.approve ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
                {reviewMutation.isLoading ? 'Processing...' : (modal.approve ? 'Approve' : 'Reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
